/**
 * variant-picker.js
 *
 * Consolidated product-page controller. Replaces inline product-page JS.
 *
 * Responsibilities:
 *   - Wire each <form class="product-form"> on the page.
 *   - On variant radio change: resolve selected variant by matching all
 *     option values in order, then:
 *       1. Update hidden input[name="id"]
 *       2. Update add-to-cart button (label + enabled state)
 *       3. Swap the main gallery image to the variant's featured_media.id
 *          by simulating a click on the corresponding thumbnail (reuses
 *          the existing thumbnail-click pipeline — does not create a
 *          parallel image-swap mechanism).
 *       4. Update history (?variant=<id>) so the URL is shareable.
 *   - On initial page load: if URL has ?variant=<id>, the inline picker
 *     state already reflects it; fire one synthetic 'change' so the
 *     gallery picks up the right image.
 *   - Thumbnail click: swap main image src + toggle active class.
 */
(function () {
  'use strict';

  function init() {
    initThumbnails();
    document.querySelectorAll('form.product-form').forEach(initVariantPicker);
  }

  /* ---------- IRO-102: size ordering (smallest → largest) ---------- */

  /*
   * Shopify renders variant options in import/creation order, so size
   * buttons come out inconsistently (e.g. M, S, XL, L). We sort the chips
   * within any "size" option client-side. Matching in initVariantPicker is
   * value-based, so reordering the DOM is safe and doesn't touch the cart.
   *
   * sizeRank returns a sortable number covering: one-size, youth (Y*),
   * alpha (XXS…XXXXXL incl. 2XL/3XL/X-LARGE synonyms), and numeric
   * (pants/boots). Unknowns fall to the end keeping their original order.
   */
  var ALPHA_ORDER = ['XXXS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', 'XXXXL', 'XXXXXL'];
  // Includes the 3-letter abbreviations the WPS/HJC catalog uses
  // (XSM/SML/MED/LRG/XLG) plus common spellings.
  var SIZE_SYNONYMS = {
    'EXTRASMALL': 'XS', 'XSMALL': 'XS', 'XSM': 'XS',
    'SMALL': 'S', 'SML': 'S', 'SM': 'S',
    'MEDIUM': 'M', 'MED': 'M', 'MD': 'M',
    'LARGE': 'L', 'LRG': 'L', 'LG': 'L',
    'EXTRALARGE': 'XL', 'XLARGE': 'XL', 'XLG': 'XL',
    'ONESIZE': 'OS', 'OSFM': 'OS', 'OS': 'OS', 'ONESIZEFITSALL': 'OS', 'ONESIZEFITSMOST': 'OS'
  };

  function sizeRank(raw) {
    var s = String(raw == null ? '' : raw).toUpperCase().replace(/[\s_]+/g, '').replace(/-/g, '');
    if (SIZE_SYNONYMS[s]) s = SIZE_SYNONYMS[s];
    if (s === 'OS') return -1; // one-size first

    // Youth prefix (YXS, YS, YOUTHL, …) ranks before adult.
    var youth = false;
    if (/^YOUTH/.test(s)) { youth = true; s = s.replace(/^YOUTH/, ''); }
    else if (/^Y(XS|XXS|S|M|L|XL|XXL)$/.test(s)) { youth = true; s = s.slice(1); }

    // Numeric-prefixed forms: 2XL→XXL, 3XS→XXXS, 2X→XX.
    var m = s.match(/^(\d+)X([SL])$/);
    if (m) s = new Array(parseInt(m[1], 10) + 1).join('X') + m[2];
    var m2 = s.match(/^(\d+)X$/);
    if (m2) s = new Array(parseInt(m2[1], 10) + 1).join('X');

    var idx = ALPHA_ORDER.indexOf(s);
    if (idx !== -1) return (youth ? 100 : 1000) + idx;

    if (/^\d+(\.\d+)?$/.test(s)) return 2000 + parseFloat(s); // numeric sizes

    return 9000; // unknown → end (stable sort preserves original order)
  }

  function sortSizeOptions(form) {
    form.querySelectorAll('.variant-option').forEach(function (fieldset) {
      var legend = fieldset.querySelector('.variant-option__label');
      if (!legend || !/size/i.test(legend.textContent)) return;
      var wrap = fieldset.querySelector('.variant-option__values');
      if (!wrap) return;
      var chips = Array.prototype.slice.call(wrap.querySelectorAll('.variant-chip'));
      if (chips.length < 2) return;

      chips
        .map(function (chip, i) {
          var input = chip.querySelector('input[type="radio"]');
          var val = input ? input.value : chip.textContent;
          return { chip: chip, key: sizeRank(val), idx: i };
        })
        .sort(function (a, b) {
          return a.key !== b.key ? a.key - b.key : a.idx - b.idx;
        })
        .forEach(function (item) { wrap.appendChild(item.chip); });
    });
  }

  /* ---------- Thumbnail strip ---------- */

  function initThumbnails() {
    var featured = document.querySelector('.product-media__featured img, img.product-media__featured');
    // Image snippet may wrap <img> in a <div class="image product-media__featured"> —
    // querySelectorAll above handles both shapes.
    if (!featured) {
      var wrapper = document.querySelector('.product-media__featured');
      if (wrapper) featured = wrapper.tagName === 'IMG' ? wrapper : wrapper.querySelector('img');
    }
    if (!featured) return;

    var main = document.getElementById('ProductMediaMain');
    var thumbs = document.querySelectorAll('.product-media__thumb');

    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        activateThumb(btn, thumbs, featured, main);
      });
    });
  }

  function activateThumb(btn, thumbs, featured, main) {
    if (!btn) return;
    var url = btn.getAttribute('data-image-url');
    if (url && featured) {
      featured.src = url;
      // Some lazy-load libs read srcset; nuke it so the src takeover sticks.
      featured.removeAttribute('srcset');
    }
    if (main) {
      main.setAttribute('data-media-id', btn.getAttribute('data-media-id') || '');
    }
    thumbs.forEach(function (b) { b.classList.remove('product-media__thumb--active'); });
    btn.classList.add('product-media__thumb--active');
  }

  /* ---------- Variant picker ---------- */

  function initVariantPicker(form) {
    // IRO-102: reorder size chips smallest→largest before wiring anything.
    sortSizeOptions(form);

    var dataEl = form.querySelector('script[data-product-variants]');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('[variant-picker] could not parse variants JSON', e);
      return;
    }
    if (!Array.isArray(variants) || variants.length === 0) return;

    var hiddenIdInput = form.querySelector('input[name="id"]');
    var addBtn = form.querySelector('.product-form__add-btn');
    // IRO-127: the displayed price must follow the selected variant. It lives
    // in .product-info__price — a sibling of the form under .product-info.
    var priceContext = form.closest('.product-info') || document;
    var priceEl = priceContext.querySelector('.product-info__price .price');
    // IRO-71: discount display elements (compare-at strikethrough, % off, $ saved)
    var comparePriceEl = priceContext.querySelector('.product-info__price [data-compare-price]');
    var savingsEl = priceContext.querySelector('.product-info__price [data-savings]');
    var savingsPctEl = priceContext.querySelector('.product-info__price [data-savings-pct]');
    var savingsAmtEl = priceContext.querySelector('.product-info__price [data-savings-amt]');
    // IRO-173: SKU line under the product name — kept in sync per variant.
    var skuEl = priceContext.querySelector('[data-variant-sku]');

    // Desktop gallery thumbnails + main image (display:none on mobile).
    var thumbs = document.querySelectorAll('.product-media__thumb');
    var main = document.getElementById('ProductMediaMain');
    var featured = (function () {
      var wrapper = document.querySelector('.product-media__featured');
      if (!wrapper) return null;
      return wrapper.tagName === 'IMG' ? wrapper : wrapper.querySelector('img');
    })();

    /*
     * IRO-84: also reach the mobile scroll-snap carousel. The desktop
     * gallery is display:none below 900px so updating it has no visible
     * effect on phones — we ALSO need to scroll the mobile carousel to
     * the slide matching the selected variant's media.
     */
    var mobileCarousel = document.getElementById('ProductCarousel');
    var mobileSlides = mobileCarousel
      ? mobileCarousel.querySelectorAll('.product-media__slide')
      : [];
    var mobileDots = document.querySelectorAll('.product-media__dot');

    function selectedOptionValues() {
      var radios = form.querySelectorAll('input[type="radio"][name^="option"]:checked');
      // Sort by name so option1, option2, … land in declared order regardless
      // of DOM traversal order (some browsers iterate inconsistently).
      return Array.from(radios)
        .sort(function (a, b) { return a.name.localeCompare(b.name); })
        .map(function (r) { return r.value; });
    }

    function findVariant(selected) {
      return variants.find(function (v) {
        if (!Array.isArray(v.options) || v.options.length !== selected.length) return false;
        return v.options.every(function (o, i) { return o === selected[i]; });
      });
    }

    function updateAddButton(variant) {
      if (!addBtn) return;
      addBtn.disabled = !variant.available;
      /* IRO-97: keep the label as just "Add to cart" — IRO-87 dropped the
         price suffix from the server-rendered markup; the variant picker must
         respect that on every variant switch. The price is shown separately
         in .product-info__price and is kept in sync by updatePrice(). */
      addBtn.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }

    /*
     * IRO-127: keep the displayed price in sync with the selected variant.
     * Previously the picker updated the cart id, button, image and URL but
     * never the price, so the PDP stayed on the default variant's price
     * (e.g. an adult $499.95 even after choosing the $474.95 Youth size) —
     * checkout charged the right amount, but the page misled the shopper.
     * Uses the Liquid-formatted money strings emitted in the variant JSON so
     * we don't reimplement the shop's money format in JS.
     */
    function updatePrice(variant) {
      if (!priceEl || variant.price_money == null) return;
      priceEl.textContent = variant.price_money;
      if (variant.price_amount != null) {
        priceEl.setAttribute('content', variant.price_amount); // keep schema.org price accurate
      }

      /*
       * IRO-71: keep the discount display in sync with the selected variant —
       * compare-at strikethrough, "X% Off" badge, and "Save $Y". One variant
       * may be on sale while another isn't, so toggle visibility each change.
       */
      var onSale = variant.compare_at_money != null && variant.savings_money != null;
      if (comparePriceEl) {
        comparePriceEl.textContent = onSale ? variant.compare_at_money : '';
        comparePriceEl.hidden = !onSale;
      }
      if (savingsEl) savingsEl.hidden = !onSale;
      if (onSale) {
        if (savingsPctEl) savingsPctEl.textContent = variant.discount_pct + '% Off';
        if (savingsAmtEl) savingsAmtEl.textContent = 'Save ' + variant.savings_money;
      }
    }

    /* IRO-173: keep the SKU line in sync with the selected variant. The element
       only exists when the initial variant had a SKU; if a variant has none,
       hide the line rather than showing a stale value. */
    function updateSku(variant) {
      if (!skuEl) return;
      if (variant.sku) {
        skuEl.textContent = 'SKU: ' + variant.sku;
        skuEl.hidden = false;
      } else {
        skuEl.hidden = true;
      }
    }

    function scrollMobileCarouselTo(mediaId) {
      if (!mobileCarousel || !mobileSlides.length || !mediaId) return false;
      var targetSlide = Array.from(mobileSlides).find(function (slide) {
        return slide.getAttribute('data-media-id') === String(mediaId);
      });
      if (!targetSlide) return false;
      try {
        mobileCarousel.scrollTo({
          left: targetSlide.offsetLeft,
          behavior: 'smooth',
        });
      } catch (_) {
        mobileCarousel.scrollLeft = targetSlide.offsetLeft;
      }
      if (mobileDots && mobileDots.length) {
        var targetIndex = Array.from(mobileSlides).indexOf(targetSlide);
        mobileDots.forEach(function (dot, i) {
          dot.classList.toggle('product-media__dot--active', i === targetIndex);
        });
      }
      return true;
    }

    /*
     * IRO-84 v7: handle Shopify's two serialization shapes for
     * variant.featured_image. Depending on Liquid version, the | json
     * filter on an Image drop can emit either:
     *   - an object  { id, src, alt, width, height, ... }
     *   - a URL string  "https://cdn.shopify.com/.../image.jpg"
     * The 2026 EXO-R430 in this store emits the string form, which
     * made variant.featured_image.id evaluate to undefined and broke
     * every code path that read .id or .src off the image.
     *
     * imageUrlOf() normalizes featured_image to a URL string or null.
     * featured_media is always an object {id} since we hand-roll it
     * in product.liquid, so it remains the preferred (media-id) path.
     */
    function imageUrlOf(img) {
      if (!img) return null;
      if (typeof img === 'string') return img;
      if (typeof img.src === 'string') return img.src;
      return null;
    }

    function setMobileFirstSlideImage(featuredImage) {
      if (!mobileSlides.length || !featuredImage) return;
      var src = imageUrlOf(featuredImage);
      if (!src) return;
      var firstSlide = mobileSlides[0];
      var img = firstSlide.querySelector('img');
      if (!img) return;
      img.src = src;
      img.removeAttribute('srcset');
      if (featuredImage.alt) img.alt = featuredImage.alt;
      try {
        mobileCarousel.scrollTo({ left: 0, behavior: 'smooth' });
      } catch (_) {
        mobileCarousel.scrollLeft = 0;
      }
    }

    /*
     * IRO-123: deterministic, single-pass image sync. Desktop and mobile are
     * updated independently so neither clobbers the other.
     *
     * The previous two-path version (featured_media, then featured_image as a
     * second pass) could BOTH run on one change: the featured_image pass would
     * overwrite the main <img> that the featured_media pass had just set
     * correctly, and it matched the featured_image *image id* against thumbs
     * keyed by *media id* (different id-spaces) so it could never set the
     * highlight — leaving swatch / main image / active thumbnail desynced
     * (e.g. main on Black/White while the highlight stayed on Red).
     *
     * Match key is the MEDIA id (variant.featured_media.id) — what both the
     * desktop thumbs and mobile slides are keyed on (data-media-id). The
     * featured_image URL is only a fallback for the main <img> when the
     * variant's media has no rendered thumb or featured_media is absent.
     */
    function swapImage(variant) {
      var mediaId = (variant.featured_media && variant.featured_media.id)
        ? String(variant.featured_media.id)
        : null;
      var fimg = variant.featured_image;
      var fimgUrl = imageUrlOf(fimg);

      // --- Desktop: exactly one authoritative update ---
      var desktopThumb = mediaId
        ? Array.from(thumbs).find(function (b) {
            return b.getAttribute('data-media-id') === mediaId;
          })
        : null;

      if (desktopThumb) {
        // The matching thumb carries the variant image and becomes active —
        // single source of truth for both the main <img> and the highlight.
        activateThumb(desktopThumb, thumbs, featured, main);
      } else if (featured && fimgUrl) {
        // No matching thumb (media past the strip cap, or no featured_media).
        // Set the main image directly and clear any stale highlight so the
        // active thumbnail never points at the wrong color.
        featured.src = fimgUrl;
        featured.removeAttribute('srcset');
        if (main) main.setAttribute('data-media-id', mediaId || '');
        thumbs.forEach(function (b) {
          b.classList.remove('product-media__thumb--active');
        });
      }

      // --- Mobile carousel: independent of the desktop update ---
      var mobileScrolled = mediaId ? scrollMobileCarouselTo(mediaId) : false;
      if (!mobileScrolled && fimgUrl) {
        setMobileFirstSlideImage(fimg);
      }
    }

    function updateUrl(variant) {
      if (!window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());
    }

    function onChange() {
      var selected = selectedOptionValues();
      if (selected.length === 0) return;
      var variant = findVariant(selected);
      if (!variant) return;

      if (hiddenIdInput) hiddenIdInput.value = variant.id;
      updateAddButton(variant);
      updatePrice(variant);
      updateSku(variant);
      swapImage(variant);
      updateUrl(variant);
    }

    form.addEventListener('change', function (e) {
      var target = e.target;
      if (!target || target.tagName !== 'INPUT') return;
      if (target.type !== 'radio') return;
      if (!/^option\d+$/.test(target.name)) return;
      onChange();
    });

    /*
     * IRO-84 v3: explicit tap handler on .variant-chip labels.
     *
     * Native label→radio click forwarding is unreliable on some Android
     * Chrome versions when the radio is visually hidden — taps register
     * on the label but the browser never flips the radio's checked
     * state, so no change event fires and onChange() never runs.
     *
     * Belt-and-suspenders: handle the click ourselves. If the chain
     * already worked (input is checked), skip. Otherwise manually
     * uncheck siblings, check this one, and dispatch change.
     */
    form.querySelectorAll('.variant-chip').forEach(function (label) {
      var input = label.querySelector('input[type="radio"]');
      if (!input) return;

      label.addEventListener('click', function (e) {
        if (input.disabled) return;
        if (input.checked) return; // already selected — no work

        // Uncheck other radios in the same group (browser would normally
        // do this automatically when a radio in the group is checked).
        form.querySelectorAll('input[type="radio"][name="' + input.name + '"]').forEach(function (r) {
          r.checked = false;
        });

        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));

        // Prevent the browser's own label-click forwarding from running
        // after us — would otherwise toggle the radio back off and fire
        // a second change event with no net state change.
        e.preventDefault();
      });
    });

    // Initial sync — if URL has ?variant=, the radios are already checked by
    // server-rendered Liquid (selected_or_first_available_variant). Fire one
    // synthetic change so the gallery and button reflect the same variant.
    var hasVariantParam = new URL(window.location.href).searchParams.has('variant');
    if (hasVariantParam) {
      // Defer one tick so the rest of the page settles.
      setTimeout(onChange, 0);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
